import { useState, type ChangeEvent, type FormEvent } from 'react'

import {
  type ConfirmMatterRequest,
  type ExtractionReading,
  type IntakePreviewResponse,
  type IntakeRequest,
  type IntakeSource,
  type ProceedingReading,
  useConfirmMatter,
  useIntakePreview,
} from '@/api/intake'
import { useDeadlineRule } from '@/api/deadlines'
import { useOpenOpposition } from '@/api/opposition'
import {
  cleanGrounds,
  GROUND_EXAMPLES,
  pipelineRunBody,
  useCreateMatter,
  useStartPipelineRun,
  type RunDraft,
} from '@/api/pipeline'
import {
  IMPLEMENTED_PROCEEDING_TYPES,
  JURISDICTIONS,
  JURISDICTION_OFFICES,
  jurisdictionLabel,
  type Jurisdiction,
  type ProceedingType,
} from '@/api/types'
import { API_BASE, isApiError } from '@/api/client'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  ScreenHeader,
  Select,
  Textarea,
  type BadgeTone,
  useToast,
} from '@/components/ui'
import { useRouter } from '@/routes'

import { LookupSource, type LookupHandoff } from './LookupSource'

/**
 * New case — read, confirm, run.
 *
 * ## Why three steps, and why in this order
 *
 * The previous version of this screen was one long form that asked a person
 * to type the office, the grounds and both dates before anything had looked
 * at the document — which is also the complaint that started this rewrite:
 * unclear what to fill in, unclear why, unclear what the platform was going
 * to do with it. I1 already names the right shape — *"Extraction prefills; a
 * human confirms … before the expensive stages run"* (REQ-20) — so this
 * screen is now three separate moments instead of one form pretending to be
 * a single step:
 *
 * 1. **Read** (`POST /api/intake/preview`) — paste, upload a PDF, or give a
 *    link, and the platform reads what it can: is the text legible, what
 *    language, and a reconstruction's best guess at office/grounds/kind.
 *    Nothing is created yet. A scan with no text layer is OCR'd here if the
 *    deployment has it, and the result is marked low-confidence — never
 *    treated as settled.
 * 2. **Confirm** — the prefilled reading, editable. Office, grounds, mark,
 *    applicant, application number and the printed deadline all come from a
 *    label on the document; an empty field means the papers printed none, not
 *    that nothing looked.
 *    Submitting here creates the matter and immediately confirms it
 *    (`POST /api/matters` then `POST /api/matters/{id}/confirm`), which is
 *    also the moment the matter leaves `INTAKE` — no pipeline run is
 *    possible before this (409 `matter_not_confirmed`).
 * 3. **Run** — case facts and the generate toggle, exactly as before. A dry
 *    run costs nothing; a generate run is one live completion, stated as
 *    such rather than hidden behind a button.
 *
 * Everything a machine read is captioned as read, and everything a person
 * types is theirs to correct — that distinction is the whole of I1 and it is
 * preserved field by field, the same discipline the single-page form used to
 * apply to a blanker canvas.
 *
 * ## Which door the document points at
 *
 * Step 2 also asks which proceeding this is. A Madrid provisional refusal
 * arrives in one envelope whether the office raised the objection itself or
 * somebody has already opposed the mark, and the refusal type the register
 * prints does not carry the difference: two of twelve real notifications in
 * the diagnostic sample were oppositions, nothing here noticed, and an
 * attorney read the whole PDF to find the opponent's name and re-type it.
 * `server/app/documents/proceeding.py` reads that off the text, and this
 * screen prefills the door and the opponent from it on exactly the terms
 * office and grounds already use — only when the document *said* so, always as
 * controls a person can change, always beside the line it was read from. An
 * unconfident reading changes nothing: the examination door is the honest
 * default for "the document did not say", not a guess worth acting on.
 *
 * A blank opponent box is not one failure mode. A sweep of the full
 * 554-document fixture set found 165 (30%) confidently OPPOSITION with no
 * name this reader could take, and every one checked was EUIPO or UKIPO's own
 * template saying, verbatim, that the opponent's identity went out in a
 * SEPARATE notice — not a reading that came up short. Before
 * `opponent_notified_separately` existed, that arrived here indistinguishable
 * from an ordinary "extraction missed it": the same empty box, the same
 * generic required-field message, no way to tell a structural gap (fixed only
 * by the second document) from a reading bug (fixed by a better regex). This
 * screen now tells the two apart — see `opponentNotifiedSeparately` below.
 */

type Step = 'read' | 'confirm' | 'run'
type SourceKind = 'paste' | 'upload' | 'url' | 'lookup'

const READABILITY_TONE: Record<string, BadgeTone> = {
  READABLE: 'ok',
  NO_TEXT: 'block',
  GARBLED: 'block',
  NOT_LATIN_SCRIPT: 'warn',
}

const LANGUAGE_TONE: Record<string, BadgeTone> = {
  DRAFT: 'ok',
  DECLINE_NON_ENGLISH: 'block',
  HOLD: 'warn',
}

/**
 * The two doors, and the only two `server/app/documents/proceeding.py` reads.
 *
 * Not {@link IMPLEMENTED_PROCEEDING_TYPES}, which is the narrower P0 list: an
 * opposition create is implemented — it is what `OppositionScreen` posts — and
 * the deployments that turn it off (`TMVERSE_OPPOSITION_ENABLED`, default on)
 * answer 501 in their own words, which reaches the analyst as the toast below.
 * A refusal that explains itself beats an option silently missing from a
 * select. Cancellation and appeal are not offered because nothing reads them
 * off a document and nothing drafts them.
 */
const WIZARD_PROCEEDINGS: readonly (readonly [ProceedingType, string])[] = [
  ['EXAM_OA', 'Examination report — the office raised the objection itself'],
  ['OPPOSITION', 'Opposition — somebody has already filed against the mark'],
]

const SOURCE_WORDS: Record<IntakeSource, string> = {
  paste: 'pasted text',
  upload: 'uploaded PDF',
  url: 'PDF from a link',
}

// `reading.method` ('text_layer', 'ocr', ...) is the reader's own code, not
// something a person decides on, so it stays out of the sentence. The one fact
// in it that matters to a reader of the result is OCR, and `ocr_used` carries it.
function readingSummary(reading: IntakePreviewResponse): string {
  const bits: string[] = [SOURCE_WORDS[reading.source] ?? reading.source]
  if (reading.ocr_used) bits.push('read by OCR')
  if (reading.page_count) bits.push(`${reading.page_count} page${reading.page_count === 1 ? '' : 's'}`)
  return bits.join(' · ')
}

/**
 * "Open the PDF" / "Open at the register", side by side.
 *
 * Both open in a **new tab**, and that is the whole point: checking whether the
 * platform read the right document is something an attorney does *beside* this
 * form, with the form still holding its half-typed corrections. Navigating away
 * and back would lose them.
 *
 * The PDF link goes to the matter's own blob route, which sends a PDF
 * `Content-Disposition: inline`, so it renders in the tab instead of landing in
 * Downloads. The session cookie is `SameSite=Lax`, which a top-level navigation
 * carries, so an authenticated route is reachable this way whether or not the
 * API shares an origin with this page.
 *
 * `rel="noreferrer"` on the register link: that one leaves for somebody else's
 * site, and the URL of a page naming a client's matter is not theirs to log.
 */
function VerifyLinks({
  documentHref,
  registerHref,
}: {
  documentHref: string
  registerHref: string
}) {
  if (!documentHref && !registerHref) return null
  return (
    <div className="flex flex-wrap items-center gap-3 text-meta">
      {documentHref ? (
        <a
          className="font-medium underline underline-offset-2"
          href={documentHref}
          target="_blank"
          rel="noreferrer"
        >
          Open the refusal PDF ↗
        </a>
      ) : null}
      {registerHref ? (
        <a
          className="font-medium underline underline-offset-2"
          href={registerHref}
          target="_blank"
          rel="noreferrer"
        >
          Open the case at the register ↗
        </a>
      ) : null}
      <span className="text-muted">Opens in a new tab; this form keeps what you have typed.</span>
    </div>
  )
}

export function NewCaseScreen() {
  const router = useRouter()
  const toast = useToast()
  const preview = useIntakePreview()
  const createMatter = useCreateMatter()
  // Two hooks onto one endpoint, deliberately. `POST /api/matters` refuses an
  // examination create carrying `opponent_name` (422
  // `opponent_on_examination_matter`) *and* an opposition create without one
  // (422 `opponent_required`), so `api/opposition.ts` keeps the two request
  // bodies as two types rather than one with everything optional — see its own
  // note on why. Going through that type here is what stops this screen
  // sending either mistake: neither compiles.
  const openOpposition = useOpenOpposition()
  const confirmMatter = useConfirmMatter()
  const startRun = useStartPipelineRun()

  const [step, setStep] = useState<Step>('read')

  // --- Step 1: read -------------------------------------------------------
  const [sourceKind, setSourceKind] = useState<SourceKind>('paste')
  const [pasteText, setPasteText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [documentUrl, setDocumentUrl] = useState('')
  const [reading, setReading] = useState<IntakePreviewResponse | null>(null)
  const [readSubmitted, setReadSubmitted] = useState(false)

  // --- Step 2: confirm -----------------------------------------------------
  const [documentText, setDocumentText] = useState('')
  const [office, setOffice] = useState<Jurisdiction>('IN')
  const [groundsText, setGroundsText] = useState('')
  const [applicationNumber, setApplicationNumber] = useState('')
  const [markText, setMarkText] = useState('')
  const [applicantName, setApplicantName] = useState('')
  const [deadlineDate, setDeadlineDate] = useState('')
  // Which door, and who is on the other side of it. Prefilled from the
  // reading exactly as office and grounds are, and — like them — a control on
  // the form rather than hidden state: a proceeding type nobody can see is a
  // proceeding type nobody can correct (I1).
  const [proceedingType, setProceedingType] = useState<ProceedingType>('EXAM_OA')
  const [opponentName, setOpponentName] = useState('')
  const [confirmSubmitted, setConfirmSubmitted] = useState(false)
  const [matterId, setMatterId] = useState<string | null>(null)
  // Set the moment `createMatter` succeeds, independently of `matterId`
  // (which waits for confirm too): if confirm then fails, this is what a
  // retry reuses instead of calling `createMatter` again — otherwise every
  // retry after a failed confirm left the previous attempt's matter behind,
  // created but never confirmed, with no way back to it from this screen.
  const [pendingMatterId, setPendingMatterId] = useState<string | null>(null)
  //: The landed document does not name the office this matter is for. Held as
  //: state rather than left to the toast that also reports it, because a toast
  //: is gone by the time anyone reads the grounds — and the grounds are exactly
  //: what looks plausible and wrong in that case: a EUIPO refusal landed on an
  //: India matter fills this form with EUTMR Article 7 and EUIPO's time limit,
  //: every box individually valid.
  const [documentOfficeMismatch, setDocumentOfficeMismatch] = useState(false)
  //: Where to *look* at what was landed, rather than take the reading's word
  //: for it. Both open in a new tab so the form keeps its state and its
  //: half-typed corrections: verifying a document is a thing you do beside the
  //: form, not instead of it.
  //:
  //: The PDF is served by the matter's own blob route, which sends a PDF
  //: `Content-Disposition: inline` — so it renders in the tab rather than
  //: landing in Downloads. The register link is the page the record was read
  //: from, which is the only place the *other* documents on the case can be
  //: seen.
  const [documentHref, setDocumentHref] = useState('')
  const [registerHref, setRegisterHref] = useState('')

  // --- Step 3: run -----------------------------------------------------------
  const [reportDate, setReportDate] = useState('')
  const [printedDeadline, setPrintedDeadline] = useState('')
  //: What the last reading *counted* rather than read, and the date it printed
  //: its issue date under. Kept apart from the boxes they fill so the screen
  //: can say where a value came from: a US action prints "within three months
  //: of the Issue date" and no reply date, and a box labelled "as printed"
  //: that held the sum was telling a reviewer to look for a date that is not
  //: on any page. Whether the box *still* holds it is decided by comparing,
  //: so a person who types over the value drops the note with it.
  const [countedDeadline, setCountedDeadline] = useState<{ date: string; quote: string } | null>(
    null,
  )
  const [readReport, setReadReport] = useState<{ date: string; quote: string } | null>(null)
  //: Whether the deadline that was *confirmed* was counted rather than printed,
  //: so the run step does not describe an empty box as one "carried" from it.
  const [confirmedCounted, setConfirmedCounted] = useState(false)
  const [caseFacts, setCaseFacts] = useState('')
  const [generate, setGenerate] = useState(false)

  const grounds = cleanGrounds(groundsText.split(/[\n,]/))
  const pasteEmpty = pasteText.trim().length === 0
  const urlEmpty = documentUrl.trim().length === 0
  const documentMissing = documentText.trim().length === 0
  //: The reading said the mark is a device. Read off the reading rather than
  //: off `markText` being empty, because those are the two states this has to
  //: tell apart.
  const markIsFigurative = reading?.extraction.mark.device ?? false
  //: The fields the document did not print, each with the reason — so an empty
  //: box reads as a gap the platform *knows about* rather than a field it
  //: failed at. Measured across 30 real refusals (10 each EM/GB/IN): office
  //: 30/30, grounds 29/30, number 29/30, applicant 29/30, but mark 17/30 and
  //: deadline 17/30 — and every India miss on those last two is the form
  //: itself, not the reader. India's Rule 17(1) notification has no mark
  //: field at all, and states its time limit as a period ("one month from the
  //: date of receipt") rather than a date. Silence on that is what made the
  //: first screen read as broken when it was correct.
  const notPrinted: readonly { field: string; why: string }[] = reading
    ? [
        !markText.trim() && !markIsFigurative
          ? {
              field: 'Mark',
              why: 'no mark is printed on this notification — many Madrid forms identify the case by number and holder only',
            }
          : null,
        !deadlineDate.trim()
          ? {
              field: 'Deadline as printed',
              why: 'the papers state a period rather than a date, or print none — give the date on the report below and the rules compute it',
            }
          : null,
        !applicantName.trim()
          ? { field: 'Applicant', why: 'no holder is printed under a label' }
          : null,
        !applicationNumber.trim()
          ? { field: 'Application number', why: 'no registration number is printed under a label' }
          : null,
      ].filter((entry): entry is { field: string; why: string } => entry !== null)
    : []
  //: The report date now also drives the confirm step, not only the run step.
  //: It is what turns a *period* into a date: India states its time limit as
  //: "one month from the date of receipt" and prints no deadline at all, so
  //: without it the box stays empty on every Indian refusal and the screen
  //: reads as having failed at something it did correctly.
  const deadlineRule = useDeadlineRule(office, proceedingType, reportDate)
  //: Only offered when the papers printed no date of their own — a computed
  //: date must never quietly displace one somebody can read off the document.
  const computedDue =
    !deadlineDate.trim() && deadlineRule.data?.registered ? deadlineRule.data.due_date : null
  //: The box still holds the date the reading counted, so it is not "as
  //: printed" and says so.
  const deadlineIsCounted = countedDeadline !== null && deadlineDate === countedDeadline.date
  const reportIsRead = readReport !== null && reportDate === readReport.date
  //: What the *form* says, which is what gets sent. Not the same question as
  //: what the document said — a person is free to disagree with the reading,
  //: and this is the side of that disagreement the API sees.
  const isOpposition = proceedingType === 'OPPOSITION'
  //: An opposition with no named opponent is refused by the server (422
  //: `opponent_required`) because a second party nobody named cannot be
  //: conflict-checked. Said here, before the submit, rather than let a person
  //: meet it as an error from an endpoint.
  const opponentMissing = isOpposition && opponentName.trim().length === 0
  //: What the document itself said about the door — non-null only when it
  //: actually said "an opposition has been filed", rather than when the reader
  //: fell back to the examination default. Kept separate from the two above so
  //: the evidence stays on screen after a person overrides the reading.
  const proceedingRead: ProceedingReading | null = reading?.extraction.proceeding ?? null
  const oppositionRead: ProceedingReading | null =
    proceedingRead && proceedingRead.confident && proceedingRead.type === 'OPPOSITION'
      ? proceedingRead
      : null
  //: A confident opposition whose own template says the opponent's identity
  //: went out in a SEPARATE notice — never a guess, only true when
  //: `read_proceeding`'s own EUIPO/UKIPO template match fired. Measured live:
  //: a sweep of the full 554-document fixture set found this is 30% of the
  //: whole dataset (165 documents), not a rare edge — and before this flag
  //: existed, hitting it here looked exactly like "extraction missed the
  //: name": the same empty box, no way to tell a structural gap (fixed only
  //: by the second document) from a reading bug (fixed by a better regex).
  //: Anchored on `opponentMissing`, not the reading alone, so it disappears
  //: the instant a name is typed or the door is switched back — the same
  //: terms `opponentMissing` already keys off `isOpposition`.
  const opponentNotifiedSeparately =
    opponentMissing && oppositionRead?.opponent_notified_separately === true

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    setFile(event.target.files?.[0] ?? null)
  }

  async function onRead(event: FormEvent) {
    event.preventDefault()
    setReadSubmitted(true)
    if (sourceKind === 'lookup') return
    if (sourceKind === 'paste' && pasteEmpty) return
    if (sourceKind === 'upload' && !file) return
    if (sourceKind === 'url' && urlEmpty) return

    const request: IntakeRequest =
      sourceKind === 'paste'
        ? { kind: 'paste', documentText: pasteText }
        : sourceKind === 'upload'
          ? { kind: 'upload', file: file as File }
          : { kind: 'url', documentUrl }

    try {
      const result = await preview.mutateAsync(request)
      setReading(result)
      setDocumentText(sourceKind === 'paste' ? pasteText : result.text_excerpt)
      if (result.extraction.office.confident && result.extraction.office.office) {
        setOffice(result.extraction.office.office)
      }
      prefillFromReading(result.extraction)
      setStep('confirm')
    } catch (error) {
      toast.push('Could not read the document', {
        tone: 'error',
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Everything the reading fills in, in one place so the paste/upload path and
  // the register path cannot drift apart. Each field is filled ONLY when it is
  // still empty: a value from the register, or one a person typed, has been
  // confirmed, and a reader re-deciding underneath it is what the confirm
  // boundary exists to stop (I1).
  function prefillFromReading(extraction: ExtractionReading) {
    if (extraction.grounds.grounds.length > 0) {
      setGroundsText(extraction.grounds.grounds.join('\n'))
    }
    // The date the papers print. Until this existed nothing extracted it, and
    // since GB, US and EM have no examination deadline rule either, a matter at
    // those offices reached the reconciler with no deadline from either source.
    setDeadlineDate((current) => current || (extraction.deadline.printed ?? ''))
    setCountedDeadline(
      extraction.deadline.derived && extraction.deadline.printed
        ? { date: extraction.deadline.printed, quote: extraction.deadline.quote }
        : null,
    )
    // The date the document says it was issued. It is what the deadline rule
    // counts from, and a person typing it by hand off the first line of the
    // letter was the only way it ever got into the box.
    const issued = extraction.deadline.report_date ?? ''
    setReportDate((current) => current || issued)
    setReadReport(issued ? { date: issued, quote: extraction.deadline.report_quote ?? '' } : null)
    setMarkText((current) => current || (extraction.mark.text ?? ''))
    // The holder and the registration number, where the document prints them
    // under a label. Both were typed by hand on every case until a real intake
    // came back with them empty on a document that printed both — see
    // `server/app/documents/parties.py`. Same `current ||` shape as the two
    // above: a value a person has already put in the box always wins.
    setApplicantName((current) => current || (extraction.parties.holder ?? ''))
    // The national number first, the international one second. A Madrid
    // document prints both and only the national one is the file this office
    // will prosecute; the reading keeps them apart so this choice is made here
    // rather than buried in the reader. Falls back to the IR number, which is
    // all a Rule 17(1) notification prints.
    setApplicationNumber(
      (current) =>
        current ||
        extraction.parties.application_number ||
        (extraction.parties.registration_number ?? ''),
    )
    // Which door the document belongs to. Only a *confident* opposition moves
    // it: `confident: false` means the document said nothing either way, which
    // is the ordinary examination case, and switching on that would route a
    // straightforward refusal into a proceeding that demands an opponent who
    // does not exist. Nothing here ever switches the other way — a reading
    // does not get to un-decide what a person chose (I1). The opponent follows
    // the same only-if-still-empty rule as every field above; the notice may
    // name nobody this reader can take, and then it is a person's to type.
    if (extraction.proceeding.confident && extraction.proceeding.type === 'OPPOSITION') {
      setProceedingType('OPPOSITION')
      setOpponentName((current) => current || (extraction.proceeding.opponent_name ?? ''))
    }
  }

  async function onLookupLanded({ landed, record, office: chosen }: LookupHandoff) {
    // The matter already exists, in INTAKE, with the register's documents
    // attached and its per-class goods recorded. `pendingMatterId` is the
    // wizard's own retry slot — onConfirm reuses it instead of creating a
    // second matter, which is exactly the semantics landing needs.
    setPendingMatterId(landed.matter_id)
    setDocumentOfficeMismatch(landed.document_office_mismatch)
    setDocumentHref(
      landed.document_sha256
        ? `${API_BASE}/api/matters/${encodeURIComponent(landed.matter_id)}/blobs/${encodeURIComponent(landed.document_sha256)}`
        : '',
    )
    setRegisterHref(record.source_urls?.[0] ?? '')
    setOffice(chosen)
    setApplicationNumber(record.number ?? '')
    setMarkText(record.mark ?? '')
    // The name the landing stored, not `record.owner` — WIPO's field is
    // "Name *and address* of the holder" and the whole string in an Applicant
    // box reads on every later screen as though somebody typed it there.
    // Taken from the response rather than re-split here so this box and the
    // conflict check are looking at one value.
    setApplicantName(landed.applicant_name ?? record.owner ?? '')
    const deadline = record.deadlines?.[0]?.date ?? ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(deadline)) setDeadlineDate(deadline)
    for (const note of landed.notes) {
      toast.push('From the register', { tone: 'info', description: note })
    }
    if (!landed.document_text) {
      // The register published no readable refusal (UKIPO publishes none,
      // EUIPO withholds them, USPTO keeps them behind a keyed API, or the
      // stored one is a scan). The matter is started; the letter comes from
      // the attorney's own file, through the same paste/upload the wizard
      // already has.
      setSourceKind(landed.needs_ocr ? 'upload' : 'paste')
      toast.push(`${landed.matter_id} started from the register`, {
        tone: 'success',
        description: landed.needs_ocr
          ? 'The stored refusal is a scan — upload it (or your own copy) so OCR can read it.'
          : 'No published refusal came back. Paste or upload the letter from your own file; the matter will be reused.',
      })
      return
    }
    try {
      const result = await preview.mutateAsync({
        kind: 'paste',
        documentText: landed.document_text,
      })
      setReading(result)
      setDocumentText(landed.document_text)
      prefillFromReading(result.extraction)
      setStep('confirm')
      toast.push(`${landed.matter_id} started from the register`, {
        tone: 'success',
        description: 'The refusal came back with the case. Check the reading, then confirm.',
      })
    } catch (error) {
      // The matter exists and the text is in hand; only the reading failed.
      // Fall back to paste with the text in place rather than losing either.
      setPasteText(landed.document_text)
      setSourceKind('paste')
      toast.push('Could not read the fetched document', {
        tone: 'error',
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  async function onConfirm(event: FormEvent) {
    event.preventDefault()
    setConfirmSubmitted(true)
    if (documentMissing) return
    if (opponentMissing) return

    try {
      let createdId = pendingMatterId
      if (!createdId) {
        // The examination branch is unchanged and carries no second party at
        // all. The opposition branch sends the applicant too, which the
        // examination one does not need to: `_parties_for` writes the
        // `OPPONENT` and `APPLICANT` rows in the create transaction and only
        // there, so a name that arrives at confirm instead lands on the matter
        // column with no party row behind it. The server also conflict-checks
        // the opponent during this call and refuses a name we already act for
        // (409) — that refusal is professional rather than technical, has no
        // waiver control here, and reaches the analyst through the catch below
        // in the server's own words. The Opposition screen is where a hit is
        // reviewed and acknowledged one party at a time.
        const matter = isOpposition
          ? await openOpposition.mutateAsync({
              jurisdiction: office,
              proceeding_type: 'OPPOSITION',
              opponent_name: opponentName.trim(),
              applicant_name: applicantName.trim() || null,
              application_number: applicationNumber.trim() || null,
              mark_text: markText.trim() || null,
            })
          : await createMatter.mutateAsync({
              jurisdiction: office,
              proceeding_type: IMPLEMENTED_PROCEEDING_TYPES[0],
              source_channel: 'manual',
              relationship_known: true,
              application_number: applicationNumber.trim() || null,
              mark_text: markText.trim() || null,
            })
        createdId = matter.id
        setPendingMatterId(createdId)
      }

      const confirmBody: ConfirmMatterRequest = {
        application_number: applicationNumber.trim() || null,
        mark_text: markText.trim() || null,
        applicant_name: applicantName.trim() || null,
        deadline_date: deadlineDate || null,
        source_blob_sha256: reading?.blob?.sha256 ?? null,
        source_blob_page_count: reading?.blob?.page_count ?? null,
      }
      await confirmMatter.mutateAsync({ matterId: createdId, body: confirmBody })

      setMatterId(createdId)
      // Only a date the papers print goes forward as "printed". A counted one
      // is the matter's deadline (confirmed above) but the run step reads the
      // papers itself and records how that date was reached.
      setPrintedDeadline(deadlineIsCounted ? '' : deadlineDate)
      setConfirmedCounted(deadlineIsCounted)
      setStep('run')
      toast.push(`${createdId} created and confirmed`, {
        tone: 'success',
        description: 'Its confirmed facts are saved. Set up the run below when you are ready.',
      })
    } catch (error) {
      toast.push('Could not create the matter', {
        tone: 'error',
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  async function onRun(event: FormEvent) {
    event.preventDefault()
    if (!matterId) return

    const draft: RunDraft = {
      documentText,
      grounds,
      office,
      kind: null,
      docRole: 'response',
      reportDate,
      printedDeadline,
      caseFacts,
      generate,
      variant: 'draft_v5',
      maxRetries: 1,
    }

    try {
      const run = await startRun.mutateAsync({ matterId, body: pipelineRunBody(draft) })
      toast.push(generate ? 'Draft requested' : 'Dry run finished', {
        tone: 'success',
        description: generate
          ? 'The stages ran and a draft was requested. Watch the run on the Case screen.'
          : 'The stages ran as a dry run — no model was called.',
      })
      router.navigate(`/case/${matterId}/${run.id}`)
    } catch (error) {
      const description =
        isApiError(error) && error.status === 409
          ? error.message
          : error instanceof Error
            ? error.message
            : String(error)
      toast.push('Could not start the run', { tone: 'error', description })
    }
  }

  const busyReading = preview.isPending
  const busyConfirming =
    createMatter.isPending || openOpposition.isPending || confirmMatter.isPending
  const busyRunning = startRun.isPending

  return (
    // Capped at a reading width: this is a form and a paragraph of hints, and at
    // the shell's full width both ran past 1,300px on a desktop.
    <div className="mx-auto flex max-w-[960px] flex-col gap-6">
      <ScreenHeader
        title="New case"
        description="Read the document, confirm what it says, then run the stages over it."
      />

      <ol className="flex flex-wrap items-center gap-2 text-meta" aria-label="Wizard progress">
        {(
          [
            ['read', '1. Read'],
            ['confirm', '2. Confirm'],
            ['run', '3. Run'],
          ] as const
        ).map(([key, label], index, all) => {
          const active = step === key
          const done = index < all.findIndex(([other]) => other === step)
          return (
            <li key={key}>
              {/* A finished step carries a check mark and, for a screen reader,
                  "(done)": outline-versus-grey alone is a colour-only cue. */}
              <Badge
                tone={active ? 'accent' : done ? 'outline' : 'neutral'}
                aria-current={active ? 'step' : undefined}
                className="gap-1"
              >
                {done ? <span aria-hidden="true">✓</span> : null}
                {label}
                {done ? <span className="sr-only"> (done)</span> : null}
              </Badge>
            </li>
          )
        })}
      </ol>

      {step === 'read' ? (
        <form className="flex flex-col gap-6" onSubmit={(event) => void onRead(event)}>
          <Card className="gap-4">
            <CardHeader
              title="The document"
              description="Paste the text, upload the PDF, give a link to fetch — or look the number up in its register. A lookup reads the register's public pages in the background and, where the register publishes the refusal itself (WIPO does), brings the document back with it."
            />

            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Source">
              {(
                [
                  ['paste', 'Paste text'],
                  ['upload', 'Upload PDF'],
                  ['url', 'PDF link'],
                  ['lookup', 'Look up a number'],
                ] as const
              ).map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  variant={sourceKind === key ? 'primary' : 'ghost'}
                  aria-pressed={sourceKind === key}
                  onClick={() => setSourceKind(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            {sourceKind === 'lookup' ? (
              <LookupSource onLanded={(handoff) => void onLookupLanded(handoff)} />
            ) : sourceKind === 'paste' ? (
              <Field
                label="Office action text"
                required
                hint="Paste the report as text."
                error={readSubmitted && pasteEmpty ? 'There is nothing to read without this.' : undefined}
              >
                {({ id, describedBy, invalid }) => (
                  <Textarea
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    rows={12}
                    value={pasteText}
                    onChange={(event) => setPasteText(event.target.value)}
                    placeholder="EXAMINATION REPORT under Section 11(1) of the Trade Marks Act 1999…"
                  />
                )}
              </Field>
            ) : sourceKind === 'upload' ? (
              <Field
                label="PDF file"
                required
                hint="Read straight off the upload. A scan with no text layer is OCR'd if this deployment has it — check the readiness panel if it is not."
                error={readSubmitted && !file ? 'Choose a PDF to read.' : undefined}
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    type="file"
                    accept="application/pdf"
                    onChange={onFileChange}
                  />
                )}
              </Field>
            ) : (
              <Field
                label="PDF link"
                required
                hint="An https:// link the server fetches directly. No sign-in to a registry — refused if it does not resolve to a public address."
                error={readSubmitted && urlEmpty ? 'Give a link to fetch.' : undefined}
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    type="url"
                    value={documentUrl}
                    onChange={(event) => setDocumentUrl(event.target.value)}
                    placeholder="https://…/the-refusal.pdf"
                  />
                )}
              </Field>
            )}

            <div className="flex items-center gap-3">
              {sourceKind !== 'lookup' ? (
                <Button type="submit" variant="primary" disabled={busyReading}>
                  {busyReading ? 'Reading…' : 'Read document'}
                </Button>
              ) : null}
              <Button type="button" variant="ghost" onClick={() => router.navigate('/queue')}>
                Cancel
              </Button>
            </div>
          </Card>
        </form>
      ) : null}

      {step === 'confirm' && reading ? (
        <form className="flex flex-col gap-6" onSubmit={(event) => void onConfirm(event)}>
          {/* Information, not a warning, so the plain card: the amber on this
              step is kept for the notice of what the papers did not print. */}
          <Card role="note" aria-label="What was read" className="gap-3">
            <CardHeader
              title="What was read"
              description={readingSummary(reading)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={READABILITY_TONE[reading.readability.verdict] ?? 'neutral'}>
                {reading.readability.verdict}
              </Badge>
              <Badge tone={LANGUAGE_TONE[reading.language.decision] ?? 'neutral'}>
                {reading.language.code ? reading.language.code.toUpperCase() : 'language undetermined'}
              </Badge>
              {reading.extraction.office.office ? (
                <Badge tone={reading.extraction.office.confident ? 'ok' : 'warn'}>
                  {reading.extraction.office.confident ? 'office read: ' : 'office guessed: '}
                  {reading.extraction.office.office}
                </Badge>
              ) : (
                <Badge tone="warn">office not detected</Badge>
              )}
              <Badge
                tone={
                  oppositionRead ? 'warn' : reading.extraction.proceeding.confident ? 'ok' : 'neutral'
                }
              >
                {reading.extraction.proceeding.confident ? 'proceeding read: ' : 'proceeding assumed: '}
                {reading.extraction.proceeding.type}
              </Badge>
              {reading.ocr_used ? <Badge tone="warn">OCR — low confidence</Badge> : null}
            </div>
            <p className="m-0 text-meta text-muted" title={`Reading method: ${reading.extraction.method}`}>
              Every field below is a prefill from a reconstruction, not a measured extraction — read
              it, correct anything wrong, then confirm. Nothing is saved until you submit this step.
            </p>
          </Card>

          {documentOfficeMismatch ? (
            <Card
              role="alert"
              aria-label="Refusal is from another office"
              className="gap-2 border-block-line bg-block-bg"
            >
              <CardHeader
                title="This refusal does not read as this office's"
                description="A Madrid registration is refused by each designated office separately, and this case holds more than one notification. The document below names a different office than the one selected, so its grounds and its time limit are that office's — and both look entirely ordinary in the boxes, which is why this stays on screen rather than passing as a message. The matter already exists, so nothing here undoes it: correct the document text and the fields below before running anything, or start a fresh lookup and choose the other refusal."
              />
              <VerifyLinks documentHref={documentHref} registerHref={registerHref} />
              <div>
                {/* Deliberately not "go back and fix it": landing has already
                    created the matter, and a fresh lookup makes a second one.
                    A control that implied an undo would be the same class of
                    plausible-and-wrong this banner exists to stop. */}
                <Button type="button" variant="secondary" onClick={() => setStep('read')}>
                  Start a fresh lookup instead
                </Button>
              </div>
            </Card>
          ) : null}

          <Card className="gap-4">
            <CardHeader
              title="What we read from your document"
              description="Correct anything the reader (or OCR) got wrong before this becomes what the pipeline drafts from."
            />
            <VerifyLinks documentHref={documentHref} registerHref={registerHref} />
            <Field label="Document text" required>
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={10}
                  value={documentText}
                  onChange={(event) => setDocumentText(event.target.value)}
                />
              )}
            </Field>
          </Card>

          {notPrinted.length > 0 ? (
            // `warn-300`/`warn-50` are not tokens this palette defines — the tone
            // scale is `warn-bg`/`warn-fg`/`warn-line` (tailwind.config.js), as
            // `CaseScreen.tsx`'s `AbandonedCard` already documents for the same
            // slip. Tailwind drops an unknown utility silently, so this card
            // once rendered with no colour at all.
            <Card
              role="note"
              aria-label="Not printed on this document"
              className="gap-2 border-warn-line bg-warn-bg"
            >
              <CardHeader
                title="These are not on the document — please supply them"
                description="The reading found everything else. Each line below says what the papers did not print and why, so an empty box is a known gap rather than something that failed."
              />
              <ul className="flex flex-col gap-1 text-meta text-warn-fg">
                {notPrinted.map((entry) => (
                  <li key={entry.field}>
                    <span className="font-medium">{entry.field}</span> — {entry.why}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card className="gap-4">
            <CardHeader
              title="Check these before we start"
              description="Office, grounds, mark, applicant, application number, the printed deadline and — where the document says so — the proceeding and the opponent are prefilled from the reading above and are yours to correct. Each is read from a label on the document and never guessed, so a field left empty means the papers did not print it: type it in."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Office" hint="Prefilled when the reading was confident; yours either way.">
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={office}
                    onChange={(event) => setOffice(event.target.value as Jurisdiction)}
                  >
                    {JURISDICTIONS.map((code) => (
                      <option key={code} value={code}>
                        {jurisdictionLabel(code)} — {JURISDICTION_OFFICES[code]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label="Application number" hint="Optional. Recorded on the matter.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    mono
                    value={applicationNumber}
                    onChange={(event) => setApplicationNumber(event.target.value)}
                    placeholder="Type it from the papers"
                  />
                )}
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Proceeding"
                hint="Which door this document belongs to. Prefilled only when the document itself said an opposition had been filed — never on a guess — and yours to change either way."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={proceedingType}
                    onChange={(event) => setProceedingType(event.target.value as ProceedingType)}
                  >
                    {WIZARD_PROCEEDINGS.map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {/* Only on the opposition door. An examination matter has no
                  second party, and a box for one would invite a name the
                  server refuses outright (422). */}
              {isOpposition ? (
                <Field
                  label="Opponent"
                  required
                  hint="Who filed the opposition. The conflict check runs on this name when the matter is created — it is how the platform knows who we would be acting against."
                  error={
                    confirmSubmitted && opponentMissing
                      ? opponentNotifiedSeparately
                        ? 'Still required to create the matter — this is the name the companion notice carries, not one extraction missed. See below.'
                        : 'Name the opponent. An opposition without one cannot be conflict-checked, and the server refuses it.'
                      : undefined
                  }
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      value={opponentName}
                      onChange={(event) => setOpponentName(event.target.value)}
                      placeholder="Type the opponent's name from the notice"
                    />
                  )}
                </Field>
              ) : null}
            </div>

            {/* The reading's own evidence, quoted, so the check is "find this
                line in the PDF" rather than "trust the badge". Rendered off
                the reading and not off `proceedingType`, so it stays put when
                a person disagrees with it and switches the door back. The
                "no name found" tail is skipped when `opponentNotifiedSeparately`
                covers it below — this sentence would otherwise say the same
                thing twice in two different tones back to back. */}
            {oppositionRead ? (
              <p className="m-0 text-meta text-muted">
                The document reads as an opposition somebody has already filed
                {oppositionRead.opposition_number
                  ? ` — opposition ${oppositionRead.opposition_number}`
                  : ''}
                .{' '}
                {oppositionRead.quote
                  ? `It says: “${oppositionRead.quote}” — find that line in the document before you confirm.`
                  : 'It printed no line this reader could quote — check the document before you confirm.'}
                {oppositionRead.opponent_name || opponentNotifiedSeparately
                  ? ''
                  : ' It did not name the opponent in a shape this reader would take, so that name is yours to type.'}
              </p>
            ) : null}

            {/* The distinct, explained state the docket asked for: not the same
                "opponent required" treatment as a plain extraction gap. A sweep
                of the full 554-document fixture set found 165 (30%) were this
                exact shape at EUIPO and UKIPO — a confident opposition whose own
                template says the opponent's identity was sent in a SEPARATE
                notice, not a reading that came up short. Conflating the two sent
                a reviewer looking, inside this document, for a name that was
                never going to be here. */}
            {opponentNotifiedSeparately && oppositionRead ? (
              <div
                role="note"
                // Deliberately not "Opponent…" — `getByLabelText` in the tests
                // also matches an `aria-label` directly, and that prefix would
                // collide with the Opponent field's own label, turning a single
                // regex query into "found two elements" everywhere in the suite.
                aria-label="Companion notice required"
                className="flex flex-col gap-1.5 rounded-md border border-warn-line bg-warn-bg px-3 py-2 text-meta text-warn-fg"
              >
                <span className="font-medium">
                  The opponent isn't named here on purpose, not by mistake.
                </span>
                <span>
                  This is a formal opposition notice, but EUIPO and UKIPO both send the opponent's
                  identity in a separate document instead of printing it in this letter — UKIPO calls
                  it the TM7 “Notice of Opposition”; EUIPO's own letter here names nothing further.
                  {oppositionRead.separately_notified_quote
                    ? ` It says: “${oppositionRead.separately_notified_quote}” — find that line in the document to check it yourself.`
                    : ''}
                </span>
                <span>
                  {oppositionRead.opposition_number
                    ? `This office assigned it opposition ${oppositionRead.opposition_number} — keep that number to hand `
                    : 'Keep this document to hand '}
                  when you go looking for the companion notice, from the same office or the register.
                  You still need a name here to create the matter — the conflict check has nothing to
                  run against without one — so type it once that document is in front of you.
                </span>
              </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Mark"
                hint={
                  // A figurative mark has no word element, so an empty box is
                  // the correct final state — and it looks exactly like a box
                  // nobody has got to yet. Saying which one this is turns a
                  // blank into a fact. IR1929960 landed this way: the register
                  // had no text to give, and neither does the mark.
                  markIsFigurative
                    ? 'The document describes this mark as figurative. Leave this empty if it has no word element — the emptiness is the answer.'
                    : 'Optional.'
                }
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={markText}
                    onChange={(event) => setMarkText(event.target.value)}
                    placeholder={markIsFigurative ? 'Figurative — leave empty if it has no words' : 'Type the mark from the papers'}
                  />
                )}
              </Field>
              <Field label="Applicant" hint="Optional.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={applicantName}
                    onChange={(event) => setApplicantName(event.target.value)}
                    placeholder="Type the applicant from the papers"
                  />
                )}
              </Field>
            </div>

            <Field
              label="Grounds raised"
              hint={`One per line, or comma-separated — for example ${GROUND_EXAMPLES[office]}. Prefilled from the reading; leave blank if none were found.`}
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={3}
                  value={groundsText}
                  onChange={(event) => setGroundsText(event.target.value)}
                  placeholder={GROUND_EXAMPLES[office]}
                />
              )}
            </Field>

            {grounds.length > 0 ? (
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-meta text-muted">
                <span>Will be answered:</span>
                {grounds.map((ground) => (
                  <Badge key={ground} tone="outline">
                    {ground}
                  </Badge>
                ))}
              </p>
            ) : null}

            <Field
              label="Date on the report"
              hint={[
                reportIsRead && readReport?.quote ? `Read from the papers: “${readReport.quote}”.` : '',
                deadlineRule.data && reportDate
                  ? deadlineRule.data.detail
                  : 'The date the office issued or you received the report. Where this office has a researched response period, it becomes the deadline below.',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  value={reportDate}
                  onChange={(event) => setReportDate(event.target.value)}
                />
              )}
            </Field>

            {computedDue ? (
              <div
                role="note"
                aria-label="Computed deadline"
                className="flex flex-wrap items-center gap-3 rounded-md border border-warn-line bg-warn-bg px-3 py-2 text-meta text-warn-fg"
              >
                <span>
                  The papers print no date. Under this office&rsquo;s registered period that is{' '}
                  <span className="font-medium">{computedDue}</span> — provisional until you confirm
                  it.
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDeadlineDate(computedDue)}
                >
                  Use {computedDue}
                </Button>
              </div>
            ) : null}

            <Field
              label={deadlineIsCounted ? 'Deadline (counted from the papers)' : 'Deadline as printed'}
              hint={
                deadlineIsCounted
                  ? `Not printed on the papers — worked out from “${countedDeadline?.quote ?? ''}”. Check the arithmetic, or type the date the office gives.`
                  : "The date written on the papers. Where they print none, give the date on the report above and this office's period is applied to it."
              }
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  value={deadlineDate}
                  onChange={(event) => setDeadlineDate(event.target.value)}
                />
              )}
            </Field>
          </Card>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" disabled={busyConfirming}>
              {busyConfirming ? 'Confirming…' : 'Confirm and create matter'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                // A fresh matter for whatever the user reads next — reusing
                // a matter created for the document being left behind here
                // would confirm it with unrelated facts.
                setPendingMatterId(null)
                setStep('read')
              }}
            >
              Back to read
            </Button>
          </div>
          {confirmSubmitted && documentMissing ? (
            <p className="m-0 text-meta text-block-fg">
              The document text is empty — the pipeline has nothing to read without it.
            </p>
          ) : null}
          {confirmSubmitted && opponentMissing ? (
            <p className="m-0 text-meta text-block-fg">
              {opponentNotifiedSeparately
                ? 'This is set to an opposition, and it still needs a named opponent to be created — not because extraction failed here, but because the office sent that name in a separate document (see above). Type it once that document is in hand, or set the proceeding back to an examination report — nothing has been created either way.'
                : 'This is set to an opposition and no opponent is named. Type the name above, or set the proceeding back to an examination report — nothing has been created either way.'}
            </p>
          ) : null}
        </form>
      ) : null}

      {step === 'run' && matterId ? (
        <form className="flex flex-col gap-6" onSubmit={(event) => void onRun(event)}>
          {/* Same slip as the gaps notice on the confirm step — `ok-300`/`ok-50`
              are not tokens this palette defines; `ok-bg`/`ok-fg`/`ok-line`
              are (tailwind.config.js). */}
          <Card role="note" aria-label="Matter confirmed" className="gap-2 border-ok-line bg-ok-bg">
            <CardHeader
              title={`${matterId} — confirmed`}
              description="Set up the run, or leave this page — the matter and its confirmed facts are already saved."
            />
          </Card>

          <Card className="gap-4">
            <CardHeader
              title="Run it"
              description="A dry run walks every stage except the model. Ask for a draft only when you want one."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Report date"
                hint="The date the office issued it. The deadline rule counts from here."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="date"
                    value={reportDate}
                    onChange={(event) => setReportDate(event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Deadline as printed"
                hint={
                  confirmedCounted
                    ? 'The papers print no deadline, so there is nothing to carry. The deadline you confirmed was counted from the report date, and the run counts it again itself. Type a date here only if the papers do print one.'
                    : 'Carried from the confirm step. The run compares it with the date the deadline rules compute and flags any difference.'
                }
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="date"
                    value={printedDeadline}
                    onChange={(event) => setPrintedDeadline(event.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Case facts"
              hint="What the client actually told you. The gate compares the draft against this: a use or reputation claim that is not here is reported as asserted (FACT_ASSERTED)."
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={4}
                  value={caseFacts}
                  onChange={(event) => setCaseFacts(event.target.value)}
                  placeholder="The applicant has used the mark since 2019 in respect of…"
                />
              )}
            </Field>

            <label className="flex items-start gap-3 text-body">
              <input
                type="checkbox"
                className="mt-1"
                checked={generate}
                onChange={(event) => setGenerate(event.target.checked)}
              />
              <span>
                <span className="font-medium">Generate a draft</span>
                <span className="mt-0.5 block text-meta text-muted">
                  Off: the stages run in about a second and cost nothing. On: one live completion —
                  30–60 seconds and real spend — and the gate then judges what came back. A blocking
                  deadline stops the run before the model is ever called, and costs nothing either
                  way. A document the pipeline cannot read or is not in English, though, ends the
                  matter for good the moment it is run — on or off makes no difference to that.
                </span>
              </span>
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" variant="primary" disabled={busyRunning}>
                {busyRunning
                  ? generate
                    ? 'Running — the model can take a minute…'
                    : 'Running…'
                  : generate
                    ? 'Run and draft'
                    : 'Run as a dry run'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => router.navigate(`/case/${matterId}`)}>
                Skip for now
              </Button>
              {busyRunning && generate ? (
                <span className="text-meta text-muted">
                  The run is recorded before the model is called, so this is safe to leave — the Case
                  screen will show it either way.
                </span>
              ) : null}
            </div>
          </Card>
        </form>
      ) : null}
    </div>
  )
}

export default NewCaseScreen
